import React, {useEffect, Dispatch} from 'react'
import {useIntl} from 'react-intl'
import {HStack, VStack, Text, useMultiStyleConfig, StackProps, TextProps, List, ListProps} from '@chakra-ui/react'
import {ProductType} from '_types'
import {useAnalytics, useViewport} from '_customHooks'

interface SizeSelectorProps {
    product: ProductType
    selectedSize?: string | null
    isSizeListActive?: boolean
    sizeErrorVisible: boolean
    variant?: 'wishlist'
    setSizeErrorVisible: Dispatch<React.SetStateAction<boolean>>
    setSelectedSize: Dispatch<React.SetStateAction<string | null>>
    focusFirstSize?: boolean
}

/**
 * @name SizeSelector
 *
 * @component
 *
 * @description
 * Allows users to select a size for a given product.
 *
 * @remarks
 * Behaviour can be different based on variant; wishlist does not have automatic selection of size on overlay opening.
 *
 * @param {SizeSelectorProps} props - The properties for the `SizeSelector` component.
 * @param {ProductType} props.product - The product for which sizes are being selected.
 * @param {string | null} [props.selectedSize] - Optional selected size. Can be null if no size is selected.
 * @param {boolean} props.sizeErrorVisible - If should show error message for size selection.
 * @param {boolean} [props.isSizeListActive=true] - Indicates if size list is active for toggle behaviour on mobile devices.
 * @param {boolean} [props.variant] - Variant for the component.
 * @param {Dispatch<React.SetStateAction<boolean>>} props.setSizeErrorVisible - Function to update error state.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedSize - Function to update the selected size.
 *
 * @returns {React.FC<SizeSelectorProps>} The rendered `SizeSelector` component.
 */
const SizeSelector: React.FC<SizeSelectorProps> = ({
    product,
    selectedSize,
    isSizeListActive = true,
    sizeErrorVisible,
    variant,
    setSizeErrorVisible,
    setSelectedSize,
    focusFirstSize,
}) => {
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const {analyticsEventCustom} = useAnalytics()
    const isDesktopEffective = variant === 'wishlist' ? true : !isMobile
    const styles = useMultiStyleConfig('SizeSelector', {variant}) as Record<string, StackProps & TextProps & ListProps>

    const variationGroups = product?.c_variationGroupsData
    const activeVariationGroup = variationGroups?.find((variation) => variation.default)
    const activeColorCode = activeVariationGroup?.color?.code

    const sizes =
        activeVariationGroup?.variants
            .filter((v) => v.online)
            .map((v) => ({
                id: v.ID,
                code: v.size.code,
                label: v.size.label,
                isInStock: v.availability?.inStock,
                isOnlyOneLeft: v.availability?.isOnlyOneLeft,
            })) || []

    const availableSizeLabel = formatMessage({id: 'available.size', defaultMessage: 'Available sizes'})
    const requireProductLabel = formatMessage({id: 'button.offcanvas.request.size', defaultMessage: 'Request product'})
    const onlyOneLeftLabel = formatMessage({id: 'label.pdp.only.one.left', defaultMessage: 'Only one left'})

    const setSize = (sizeId: string, isAutoSelected?: boolean) => {
        setSelectedSize(sizeId)

        const activeSizeCode = activeVariationGroup?.variants?.find((variant) => variant.ID === sizeId)?.size?.code

        if (isAutoSelected) {
            return
        }

        analyticsEventCustom({
            category: 'Size Selector',
            action: 'size select',
            label: `${sizeId} - ${activeSizeCode}`,
            buttonType: 'Quick Cart Add',
            accessType: variant !== 'wishlist' ? 'tile' : undefined,
        })

        if (sizeErrorVisible) {
            setSizeErrorVisible(false)
        }
    }

    const autoFocus = (element: HTMLElement | null) => element?.focus()

    useEffect(() => {
        if (variant !== 'wishlist') {
            // If we only have one size, select it
            if (sizes.length === 1) {
                setSize(sizes[0].id, true)
                if (sizeErrorVisible) {
                    setSizeErrorVisible(false)
                }
                return
            }
        }
    }, [sizes, isSizeListActive])

    return (
        sizes &&
        isSizeListActive && (
            <VStack {...styles.sizesRowWrapper} data-testid="size-selector">
                {isDesktopEffective && (
                    <Text {...styles.selectionTitle} {...(sizeErrorVisible ? styles.selectionTitleError : undefined)}>
                        {availableSizeLabel}
                    </Text>
                )}

                <List as={HStack} {...styles.sizesRow}>
                    {sizes?.map((size, idx) => (
                        <HStack
                            key={`${size.id}-${idx}`}
                            as="button"
                            tabIndex={size.isInStock ? 0 : -1}
                            data-element-type={'size-button'}
                            {...styles.sizeLabelWrapper}
                            {...(!size.isInStock ? styles.sizeLabelOutOfStockWrapper : {})}
                            {...(size.id === selectedSize ? styles.selectedSizeLabelWrapper : {})}
                            onClick={() => size.isInStock && setSize(size.id)}
                            ref={focusFirstSize && idx === 0 ? autoFocus : undefined}
                            aria-pressed={selectedSize === size.id}
                        >
                            <Text {...styles.sizeLabel} {...(!size.isInStock ? styles.sizeLabelOutOfStock : {})}>
                                {size.label}
                            </Text>
                            {!isDesktopEffective && !size.isInStock && activeVariationGroup?.notifyMeEnabled && (
                                <Text
                                    as="span"
                                    {...styles.labelStockInfo}
                                    onClick={() => (window.location.href = `${activeVariationGroup?.pdpUrl}?color=${activeColorCode}&size=${size.code}`)}
                                >
                                    {requireProductLabel}
                                </Text>
                            )}
                            {!isDesktopEffective && size.isInStock && size.isOnlyOneLeft && (
                                <Text as="span" {...styles.labelOnlyOneInfo}>
                                    {onlyOneLeftLabel}
                                </Text>
                            )}
                        </HStack>
                    ))}
                </List>
            </VStack>
        )
    )
}

export default SizeSelector
